/**
 * Types and static KPI/policy config for the Payment Terms Optimisation module.
 *
 * Series data (trend, buckets, mapping, suppliers) is loaded from:
 *   GET /api/showcase/payment_terms/trend/
 *   GET /api/showcase/payment_terms/buckets/
 *   GET /api/showcase/payment_terms/mapping/
 *   GET /api/showcase/payment_terms/suppliers/
 *
 * KPI logic follows the Payment_terms_working_capital template:
 *   Working capital opportunity = spend x max(0, target days - current days) / 365.
 *   Quick-win opportunity       = spend x max(0, best existing term - current days) / 365
 *                                 (align every entity to the supplier's best already-agreed term).
 */

export type PtSupplier = {
    id: string;
    supplier: string;              // supplier legal parent
    spend: number;                 // total positive spend (rolling 12m)
    currentTerm: number;           // spend-weighted avg payment term days
    targetTerm: number;            // policy target days
    gapDays: number;               // max(0, target - current)
    wcOpportunity: number;         // cash release from moving to the policy target (max case)
    quickWinOpportunity: number;   // cash release from aligning to the best existing term (min case)
    bestExistingTerm: number | null; // best already-agreed term days (capped at target), null if unmapped
    invoiceCount: number;
    immediateSpend: number;        // spend on immediate/0-day terms
    belowTargetSpend: number;      // spend on terms shorter than target
    postedAfterDueSpend: number;   // spend posted after estimated due date (AP risk)
    discountAtRiskSpend: number;   // spend posted after the discount window
    distinctTermCount: number;     // number of different payment terms in use
    multipleTerms: boolean;
    meetsPolicy: boolean;          // weighted term meets the 60-day policy
    terms: string[];               // the distinct raw payment term texts
};

export type PtTermBucket = {
    key: string;
    label: string;
    spend: number;
    suppliers: number;
    wcOpportunity: number;
    atOrAboveTarget: boolean;
};

export type PtTermMapping = {
    term: string;          // raw payment term text from the ERP
    days: number;          // normalized net term days
    discountDays: number;
    discountPct: number;
    rule: string;          // due-date rule (fixed / eom)
    eomMonths: number;
    gibberish: boolean;    // unusable free-text term
    mapped: boolean;       // successfully normalized
    suppliersUsing: number;   // suppliers with this term in use
    estSpend: number;         // supplier spend allocated equally across each supplier's terms
    approved: boolean;        // one of the approved standard terms
};

export type PtTrendPoint = {
    month: string;
    value: number; // spend-weighted avg payment term days
};

const CURRENCY = 'EUR';

export const PT_KPIS = {
    currency: CURRENCY,
    totalPositiveSpend: 2401758312.63,
    weightedAvgTerm: 47.6,
    termCoveragePct: 0.9855,
    immediateExposure: 173261011.11,
    immediateExposurePct: 0.0721,
    spendBelowTarget: 1209508748.16,
    spendBelowTargetPct: 0.5036,
    wcOpportunity: 105496465.08,
    quickWinOpportunity: 48048174.14,
    suppliersMeetingPolicyPct: 0.1385,
    supplierCount: 12151,
    transactionCount: 563710,
    suppliersWithMultipleTerms: 2639,
    multiTermSpend: 1775906946.51,
    multiTermQuickWin: 47278285.02,
    suppliersMeetingPolicy: 1683,
    distinctTermVariants: 101,
    nonStandardTermSpend: 407981993.67,
};

export const PT_POLICY = {
    targetTermDays: 60,
    approvedTerms: [0, 30, 45, 60, 90],
    scopeNote: 'Third-party suppliers only · intercompany excluded · rolling 12 months on posting date · supplier legal-parent level',
};

export const PT_PERIOD = {
    from: 'Mar 2025',
    to: 'Feb 2026',
    label: 'Rolling 12 months',
};

/** Suppliers beyond the top 250 shown in the table. */
export const PT_LONG_TAIL = {
    count: 11901,
    spend: 1109193866.79,
    wcOpportunity: 26265433.38,
    quickWinOpportunity: 7272835.01,
    invoiceCount: 381496,
};
